import { useState, useRef, useEffect } from 'react';
import { Search, MapPin, Clock, Navigation, X } from 'lucide-react';
import { LocationResult } from '../types';
import { useGeolocation, positionToLocationResult } from '../hooks/useGeolocation';

interface SearchBarProps {
  onSearch: (query: string, location?: LocationResult | string) => void;
  onLocationSearch: (query: string) => void;
  suggestedLocations: LocationResult[];
  loadingLocations: boolean;
  loading?: boolean;
  recentSearches: { query: string; location: string | null }[];
}

export function SearchBar({
  onSearch,
  onLocationSearch,
  suggestedLocations,
  loadingLocations,
  loading = false,
  recentSearches,
}: SearchBarProps) {
  const [query, setQuery] = useState('');
  const [location, setLocation] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<LocationResult | null>(null);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);
  const [showRecentSearches, setShowRecentSearches] = useState(false);
  const locationInputRef = useRef<HTMLDivElement>(null);
  const queryInputRef = useRef<HTMLDivElement>(null);
  const locationSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasAutoRequestedRef = useRef(false);
  const hasManuallyEditedRef = useRef(false);
  const hasAutoFilledRef = useRef(false);
  
  // Get user's geolocation
  const { position, locationName, loading: geolocationLoading, error: geolocationError, requestLocation } = useGeolocation();

  // Read initial query and location from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const queryParam = urlParams.get('q');
    const locationParam = urlParams.get('location');
    
    if (queryParam && queryParam.trim()) {
      setQuery(queryParam.trim());
    }
    
    if (locationParam && locationParam.trim()) {
      setLocation(locationParam.trim());
    }
  }, []);

  // Auto-request location on mount if permission was previously granted
  useEffect(() => {
    // Check if we have saved location in localStorage (means permission was granted before)
    if (!hasAutoRequestedRef.current) {
      const savedLocation = localStorage.getItem('dryv_user_location');
      if (savedLocation && !position) {
        // Permission was granted before, so request location again
        hasAutoRequestedRef.current = true;
        requestLocation();
      }
    }
  }, [position, requestLocation]); // Include dependencies but use ref to prevent multiple calls

  // Prefill location from geolocation when available (only once, on initial load)
  useEffect(() => {
    if (position && !selectedLocation && !location.trim() && !hasManuallyEditedRef.current && !hasAutoFilledRef.current) {
      const locationResult = positionToLocationResult(position, locationName);
      if (locationResult) {
        setSelectedLocation(locationResult);
        setLocation(locationResult.name);
        hasAutoFilledRef.current = true;
      }
    }
  }, [position, locationName, selectedLocation, location]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (locationInputRef.current && !locationInputRef.current.contains(e.target as Node)) {
        setShowLocationSuggestions(false);
      }
      if (queryInputRef.current && !queryInputRef.current.contains(e.target as Node)) {
        setShowRecentSearches(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (locationSearchTimeoutRef.current) {
        clearTimeout(locationSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocation(value);
    setSelectedLocation(null); // Clear selected location when user types
    hasManuallyEditedRef.current = true; // Mark that user has manually edited
    
    // Clear existing timeout
    if (locationSearchTimeoutRef.current) {
      clearTimeout(locationSearchTimeoutRef.current);
      locationSearchTimeoutRef.current = null;
    }
    
    const trimmedValue = value.trim();
    if (trimmedValue.length >= 2) {
      // Show suggestions dropdown immediately for better UX
      setShowLocationSuggestions(true);
      // Debounce location search to avoid too many API calls
      locationSearchTimeoutRef.current = setTimeout(() => {
        onLocationSearch(trimmedValue);
      }, 300); // Wait 300ms after user stops typing
    } else if (trimmedValue.length === 0) {
      // Clear everything if input is empty
      setShowLocationSuggestions(false);
    } else {
      // Show dropdown but don't search yet (waiting for more characters)
      setShowLocationSuggestions(true);
    }
  };

  const handleClearLocation = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setLocation('');
    setSelectedLocation(null);
    setShowLocationSuggestions(false);
    hasManuallyEditedRef.current = true; // Mark that user has manually cleared
    // Clear any pending location search
    if (locationSearchTimeoutRef.current) {
      clearTimeout(locationSearchTimeoutRef.current);
      locationSearchTimeoutRef.current = null;
    }
  };

  const handleLocationSelect = (loc: LocationResult) => {
    // Clear any pending location search
    if (locationSearchTimeoutRef.current) {
      clearTimeout(locationSearchTimeoutRef.current);
      locationSearchTimeoutRef.current = null;
    }
    setLocation(loc.name);
    setSelectedLocation(loc);
    setShowLocationSuggestions(false);
  };

  const handleRecentSearch = (search: { query: string; location: string | null }) => {
    setQuery(search.query);
    setLocation(search.location || '');
    setShowRecentSearches(false);
    onSearch(search.query, search.location || '');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Prevent submission if already loading or query is empty
    if (loading || !query.trim()) {
      return;
    }
    // Use selected location with coordinates if available, otherwise use location string
    const locationToSearch = selectedLocation || (location.trim() || undefined);
    onSearch(query, locationToSearch);
    setShowRecentSearches(false);
  };

  return (
    <div className="w-full">
      <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
          {/* Query Input */}
          <div ref={queryInputRef} className="relative">
            <label htmlFor="query" className="block text-xs font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5 sm:mb-2 uppercase tracking-wider">
              What are you looking for?
            </label>
            <div className="relative">
              <Search className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-neutral-400 dark:text-neutral-500" size={16} aria-hidden="true" />
              <input
                id="query"
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  if (e.target.value.trim()) {
                    setShowRecentSearches(true);
                  }
                }}
                onFocus={() => {
                  if (query.trim() && recentSearches.length > 0) {
                    setShowRecentSearches(true);
                  }
                }}
                placeholder="used cars, Toyota Camry, Honda Accord..."
                className="input pl-9 sm:pl-11 pr-3 sm:pr-4 py-2.5 sm:py-3 text-sm sm:text-base"
                aria-describedby="query-description"
              />
              <span id="query-description" className="sr-only">Search for products across multiple marketplaces</span>
            </div>
            {showRecentSearches && recentSearches.length > 0 && (
              <div role="listbox" aria-label="Recent searches" className="absolute top-full left-0 right-0 mt-2 card shadow-lg z-20 animate-slide-up">
                <div className="p-2">
                  <div className="flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 mb-1">
                    <Clock size={12} className="text-neutral-400 dark:text-neutral-500 sm:w-3.5 sm:h-3.5" aria-hidden="true" />
                    <p className="text-[10px] sm:text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Recent Searches</p>
                  </div>
                  {recentSearches.slice(0, 5).map((search, idx) => (
                    <button
                      key={idx}
                      type="button"
                      role="option"
                      onClick={() => handleRecentSearch(search)}
                      className="w-full text-left px-2 sm:px-3 py-2 sm:py-2.5 hover:bg-neutral-50 dark:hover:bg-neutral-700 rounded-lg text-xs sm:text-sm text-neutral-700 dark:text-neutral-300 transition-colors duration-200 group"
                      aria-label={`Search for ${search.query}${search.location ? ` in ${search.location}` : ''}`}
                    >
                      <div className="font-medium group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors truncate">{search.query}</div>
                      {search.location && (
                        <div className="text-[10px] sm:text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 flex items-center gap-1">
                          <MapPin size={10} className="sm:w-3 sm:h-3" aria-hidden="true" />
                          <span className="truncate">{search.location}</span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Location Input */}
          <div ref={locationInputRef} className="relative">
            <label htmlFor="location" className="block text-xs font-semibold text-neutral-700 dark:text-neutral-300 mb-1.5 sm:mb-2 uppercase tracking-wider">
              Location (optional)
            </label>
            <div className="relative">
              <MapPin className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-neutral-400 dark:text-neutral-500" size={16} aria-hidden="true" />
              <input
                id="location"
                type="text"
                value={location}
                onChange={handleLocationChange}
                onFocus={() => {
                  if (location.trim()) {
                    setShowLocationSuggestions(true);
                    // Trigger search if we have text but no suggestions yet or if suggestions are stale
                    if (suggestedLocations.length === 0 || !selectedLocation) {
                      onLocationSearch(location);
                    }
                  }
                }}
                placeholder="Houston, TX"
                className="input pl-9 sm:pl-11 pr-10 sm:pr-12 py-2.5 sm:py-3 text-sm sm:text-base"
                aria-describedby="location-description"
                aria-autocomplete="list"
                aria-expanded={showLocationSuggestions}
                aria-controls="location-suggestions"
              />
              <span id="location-description" className="sr-only">Optional location filter for nearby results</span>
              {/* Action Buttons */}
              <div className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                {/* Clear Button - Show when there's text */}
                {location.trim() && (
                  <button
                    type="button"
                    onClick={handleClearLocation}
                    className="p-1.5 sm:p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors duration-200 group z-10"
                    title="Clear location"
                  >
                    <X size={14} className="sm:w-4 sm:h-4 text-neutral-400 dark:text-neutral-500 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors" />
                  </button>
                )}
                {/* Use My Location Button - Show when no location text (allows refreshing location) */}
                {!location.trim() && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // If we already have position, use it directly
                      if (position && locationName) {
                        const locationResult = positionToLocationResult(position, locationName);
                        if (locationResult) {
                          setSelectedLocation(locationResult);
                          setLocation(locationResult.name);
                          hasManuallyEditedRef.current = false; // Reset flag so auto-fill can work again
                          hasAutoFilledRef.current = false; // Allow it to be set again
                        }
                      } else {
                        // Otherwise request location
                        hasManuallyEditedRef.current = false; // Reset flag so auto-fill will work when position arrives
                        hasAutoFilledRef.current = false; // Allow it to be set again
                        requestLocation();
                      }
                    }}
                    disabled={geolocationLoading}
                    className="p-1.5 sm:p-2 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed group z-10"
                    title={position ? "Refresh my location" : "Use my current location"}
                    aria-label={position ? "Refresh my location" : "Use my current location"}
                  >
                    {geolocationLoading ? (
                      <div className="animate-spin rounded-full h-3.5 w-3.5 sm:h-4 sm:w-4 border-2 border-neutral-400 dark:border-neutral-500 border-t-transparent" aria-hidden="true"></div>
                    ) : (
                      <Navigation size={14} className="sm:w-4 sm:h-4 text-neutral-400 dark:text-neutral-500 group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors" aria-hidden="true" />
                    )}
                  </button>
                )}
              </div>
            </div>
            {/* Geolocation Error Message */}
            {geolocationError && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{geolocationError}</p>
            )}
            {showLocationSuggestions && location.trim() && (
              <div id="location-suggestions" role="listbox" aria-label="Location suggestions" className="absolute top-full left-0 right-0 mt-2 card shadow-lg z-20 max-h-64 overflow-y-auto animate-slide-up">
                {loadingLocations ? (
                  <div className="p-4 text-center">
                    <div className="inline-block animate-spin rounded-full h-5 w-5 border-2 border-neutral-300 dark:border-neutral-600 border-t-accent-600"></div>
                    <p className="text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 mt-2">Searching locations...</p>
                  </div>
                ) : suggestedLocations.length > 0 ? (
                  suggestedLocations.map((loc, idx) => (
                    <button
                      key={idx}
                      type="button"
                      role="option"
                      onClick={() => handleLocationSelect(loc)}
                      className="w-full text-left px-3 sm:px-4 py-2.5 sm:py-3 hover:bg-neutral-50 dark:hover:bg-neutral-700 border-b border-neutral-100 dark:border-neutral-700 last:border-b-0 transition-colors duration-200 group"
                      aria-label={`Select location ${loc.name}`}
                    >
                      <div className="font-medium text-neutral-900 dark:text-neutral-100 group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors text-sm sm:text-base">{loc.name}</div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                        {parseFloat(loc.latitude).toFixed(2)}, {parseFloat(loc.longitude).toFixed(2)}
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="p-4 text-center text-xs sm:text-sm text-neutral-500 dark:text-neutral-400">
                    {location.trim().length >= 2 ? 'No locations found. Try a different search term.' : 'Type at least 2 characters to search'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!query.trim() || loading}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 sm:py-3.5 text-sm sm:text-base font-semibold rounded-xl transition-all duration-200 disabled:cursor-not-allowed disabled:pointer-events-none bg-accent-600 text-white hover:bg-accent-700 active:bg-accent-800 shadow-sm hover:shadow-md disabled:bg-neutral-400 dark:disabled:bg-neutral-600 disabled:text-neutral-200 dark:disabled:text-neutral-400 disabled:shadow-none disabled:hover:bg-neutral-400 dark:disabled:hover:bg-neutral-600"
        >
          {loading ? (
            <>
              <div className="inline-block animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-white border-t-transparent"></div>
              <span>Searching...</span>
            </>
          ) : (
            <>
              <Search size={18} className="sm:w-5 sm:h-5 flex-shrink-0" />
              <span>Search Listings</span>
            </>
          )}
        </button>
      </form>
    </div>
  );
}
